import {
  BarChartOutlined,
  LineChartOutlined,
  RiseOutlined,
  ShopOutlined,
  ShoppingOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { DatePicker } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CREATE_jwel } from "../../Config/Config";
import CustomTooltip from "./CustomToolTip";

/* ─── formatting helpers ─── */
const formatINR = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;

const Graphs = () => {
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [totalSalesGraph, setTotalSalesGraph] = useState([]);
  const [totalPurchaseGraph, setTotalPurchaseGraph] = useState([]);
  const [salesGraph, setSalesGraph] = useState([]);

  const tenantName = localStorage.getItem("tenantName");

  /* ---- data / API logic (unchanged endpoints + query params/format) ---- */
  const TotalSalesGraphAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetComodityWiseSale?billStartDate=${dayjs(
          fromDate,
        ).format("MM/DD/YYYY")}&billEndDate=${dayjs(toDate).format(
          "MM/DD/YYYY",
        )}`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      );

      const data = response.data;

      if (Array.isArray(data) && data.length > 0) {
        const transformedData = data.map((item) => ({
          date: item.PTYPE,
          amt: item.AMOUNT,
          QTY: item.QTY,
        }));
        setTotalSalesGraph(transformedData);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const TotalPurchaseGraphAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetComodityWisePurchase?billStartDate=${dayjs(
          fromDate,
        ).format("MM/DD/YYYY")}&billEndDate=${dayjs(toDate).format(
          "MM/DD/YYYY",
        )}`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      );

      const data = response.data;

      if (Array.isArray(data) && data.length > 0) {
        const transformedData = data.map((item) => ({
          date: item.PTYPE,
          amt: item.AMOUNT,
          QTY: item.QTY,
        }));
        setTotalPurchaseGraph(transformedData);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  const fetchTotalSaleAmt = async (date) => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetTotalSaleValue?billDate=${dayjs(date).format(
          "MM/DD/YYYY",
        )}`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      );

      const data = response.data;
      return Array.isArray(data) && data.length > 0 ? data[0].AMT : 0;
    } catch (error) {
      console.error("Error fetching sale amount for", date, ":", error);
      return 0;
    }
  };

  useEffect(() => {
    const loadSalesData = async () => {
      const dates = [0, 1, 2, 3].map((d) => dayjs(fromDate).subtract(d, "day"));
      const results = await Promise.all(
        dates.map((date) =>
          fetchTotalSaleAmt(date).then((amt) => ({
            date: dayjs(date).format("DD-MMM"),
            amt: amt,
          })),
        ),
      );
      setSalesGraph(results.reverse());
    };

    loadSalesData();
  }, [fromDate]);

  useEffect(() => {
    TotalSalesGraphAPI();
    TotalPurchaseGraphAPI();
  }, [fromDate, toDate]);

  /* ---- derived display-only totals (client-side sums of already-fetched data) ---- */
  const heroSalesTotal = totalSalesGraph.reduce(
    (sum, item) => sum + Number(item.amt || 0),
    0,
  );
  const heroPurchaseTotal = totalPurchaseGraph.reduce(
    (sum, item) => sum + Number(item.amt || 0),
    0,
  );
  const heroNet = heroSalesTotal - heroPurchaseTotal;

  return (
    <div className="ledger-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap');

        .ledger-page {
          --bg-deep: #0A0E1A;
          --panel-bg:
            radial-gradient(circle at 15% -10%, rgba(108, 75, 214, 0.16), transparent 50%),
            radial-gradient(circle at 100% 0%, rgba(79, 190, 145, 0.12), transparent 50%),
            linear-gradient(180deg, rgba(22, 26, 40, 0.9) 0%, rgba(12, 15, 24, 0.94) 100%);
          --card-border: rgba(255, 255, 255, 0.07);
          --grid-line: rgba(255, 255, 255, 0.08);
          --axis-text: rgba(231, 236, 245, 0.55);

          --ivory: #E7ECF5;
          --ivory-muted: rgba(231, 236, 245, 0.56);

          --teal: #4FBE91;
          --teal-soft: rgba(79, 190, 145, 0.14);
          --violet: #6C4BD6;
          --violet-soft: rgba(108, 75, 214, 0.16);
          --blue: #5B8DEF;
          --blue-soft: rgba(91, 141, 239, 0.16);
          --rose: #D6608F;
          --rose-soft: rgba(214, 96, 143, 0.16);

          --font-display: 'Fraunces', Georgia, serif;
          --font-body: 'Inter', -apple-system, sans-serif;

          position: relative;
          background: var(--bg-deep);
          color: var(--ivory);
          font-family: var(--font-body);
          padding: 28px clamp(16px, 4vw, 48px) 64px;
          border-radius: 18px;
          overflow: hidden;
          isolation: isolate;
        }

        /* soft ambient glows behind everything, cool jewel tones */
        .ledger-page::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: -1;
          background:
            radial-gradient(circle at 8% 0%, rgba(108, 75, 214, 0.14), transparent 40%),
            radial-gradient(circle at 95% 15%, rgba(79, 190, 145, 0.1), transparent 38%),
            radial-gradient(circle at 50% 100%, rgba(91, 141, 239, 0.08), transparent 45%);
        }

        .ledger-header {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: flex-end;
          gap: 20px;
          padding-bottom: 22px;
        }

        .ledger-title-row {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .ledger-title-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: var(--teal);
          background: var(--teal-soft);
          border: 1px solid rgba(79, 190, 145, 0.3);
          flex-shrink: 0;
        }

        .ledger-title {
          font-family: var(--font-display);
          font-weight: 600;
          font-size: clamp(26px, 3.4vw, 34px);
          line-height: 1.1;
          margin: 0 0 4px;
          color: var(--ivory);
        }

        .ledger-subtitle {
          margin: 0;
          font-size: 13.5px;
          color: var(--ivory-muted);
        }

        .ledger-date-range {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--card-border);
          border-radius: 12px;
          padding: 6px 14px;
        }

        .ledger-date-range .ant-picker {
          background: transparent;
          border: none;
          padding: 2px 0;
        }
        .ledger-date-range .ant-picker input {
          color: var(--ivory);
          font-family: var(--font-body);
          font-size: 13.5px;
          font-weight: 500;
          width: 92px;
        }
        .ledger-date-range .ant-picker-suffix {
          color: var(--blue);
        }

        .ledger-date-sep {
          color: var(--ivory-muted);
          font-size: 12.5px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .ledger-rule {
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.12),
            transparent
          );
          margin: 26px 0;
        }

        /* ── Hero stat cards ── */
        .ledger-hero {
          display: grid;
          grid-template-columns: repeat(3, minmax(180px, 1fr));
          gap: 18px;
        }

        .ledger-hero-card {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 18px 20px;
          border-radius: 14px;
          background: var(--panel-bg);
          border: 1px solid var(--card-border);
          box-shadow: 0 12px 28px -16px rgba(0, 0, 0, 0.6);
        }

        .ledger-hero-icon {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }

        .ledger-hero-icon--teal {
          color: var(--teal);
          background: var(--teal-soft);
          border: 1px solid rgba(79, 190, 145, 0.3);
        }
        .ledger-hero-icon--violet {
          color: var(--violet);
          background: var(--violet-soft);
          border: 1px solid rgba(108, 75, 214, 0.32);
        }
        .ledger-hero-icon--blue {
          color: var(--blue);
          background: var(--blue-soft);
          border: 1px solid rgba(91, 141, 239, 0.32);
        }
        .ledger-hero-icon--rose {
          color: var(--rose);
          background: var(--rose-soft);
          border: 1px solid rgba(214, 96, 143, 0.32);
        }

        .ledger-hero-stat {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }

        .ledger-hero-label {
          font-size: 12.5px;
          color: var(--ivory-muted);
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .ledger-hero-number {
          font-family: var(--font-display);
          font-weight: 600;
          font-size: clamp(22px, 2.6vw, 28px);
          color: var(--ivory);
          font-variant-numeric: tabular-nums;
        }

        /* ── Section headers with icon chip ── */
        .ledger-section-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-family: var(--font-display);
          font-weight: 500;
          font-size: 18px;
          color: var(--ivory);
          margin: 0 0 14px;
        }

        .ledger-section-icon {
          width: 30px;
          height: 30px;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
        }

        .ledger-section-icon--teal {
          color: var(--teal);
          background: var(--teal-soft);
        }
        .ledger-section-icon--blue {
          color: var(--blue);
          background: var(--blue-soft);
        }
        .ledger-section-icon--violet {
          color: var(--violet);
          background: var(--violet-soft);
        }

        /* ── Chart panels — dark glass, matches the hero cards now
           instead of the old bright parchment fill ── */
        .ledger-panel {
          background: var(--panel-bg);
          border: 1px solid var(--card-border);
          border-radius: 14px;
          padding: 18px 14px 6px;
          box-shadow: 0 12px 28px -16px rgba(0, 0, 0, 0.6);
        }

        @media (max-width: 640px) {
          .ledger-hero {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .ledger-date-range {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>

      <div className="ledger-header">
        <div className="ledger-title-row">
          <div className="ledger-title-icon">
            <WalletOutlined />
          </div>
          <div>
            <h1 className="ledger-title">Today's ledger</h1>
            <p className="ledger-subtitle">Sales and purchases snapshot</p>
          </div>
        </div>
        <div className="ledger-date-range">
          <DatePicker
            value={fromDate}
            onChange={setFromDate}
            inputReadOnly
            format="DD-MMM-YYYY"
            maxDate={dayjs()}
            disabledDate={(current) =>
              current && current > dayjs().endOf("day")
            }
          />
          <span className="ledger-date-sep">to</span>
          <DatePicker
            value={toDate}
            onChange={setToDate}
            inputReadOnly
            format="DD-MMM-YYYY"
            maxDate={dayjs()}
            disabledDate={(current) =>
              current && current > dayjs().endOf("day")
            }
          />
        </div>
      </div>

      <div className="ledger-hero">
        <div className="ledger-hero-card">
          <div className="ledger-hero-icon ledger-hero-icon--teal">
            <RiseOutlined />
          </div>
          <div className="ledger-hero-stat">
            <span className="ledger-hero-label">Total sale</span>
            <span className="ledger-hero-number">
              {formatINR(heroSalesTotal)}
            </span>
          </div>
        </div>

        <div className="ledger-hero-card">
          <div className="ledger-hero-icon ledger-hero-icon--violet">
            <ShoppingOutlined />
          </div>
          <div className="ledger-hero-stat">
            <span className="ledger-hero-label">Total purchase</span>
            <span className="ledger-hero-number">
              {formatINR(heroPurchaseTotal)}
            </span>
          </div>
        </div>

        <div className="ledger-hero-card">
          <div
            className={
              heroNet >= 0
                ? "ledger-hero-icon ledger-hero-icon--blue"
                : "ledger-hero-icon ledger-hero-icon--rose"
            }
          >
            <WalletOutlined />
          </div>
          <div className="ledger-hero-stat">
            <span className="ledger-hero-label">Net position</span>
            <span className="ledger-hero-number">{formatINR(heroNet)}</span>
          </div>
        </div>
      </div>

      <div className="ledger-rule" />

      <section>
        <h2 className="ledger-section-title">
          <span className="ledger-section-icon ledger-section-icon--teal">
            <BarChartOutlined />
          </span>
          Sales by commodity
        </h2>
        <div className="ledger-panel">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={
                totalSalesGraph.length
                  ? totalSalesGraph
                  : [{ date: "", amt: 0 }]
              }
              margin={{ top: 20, right: 12, left: 0, bottom: 5 }}
            >
              <defs>
                <linearGradient id="saleBarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6BDCAE" stopOpacity={1} />
                  <stop offset="100%" stopColor="#3A9E77" stopOpacity={0.85} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 13, fill: "var(--axis-text)", fontFamily: "var(--font-body)" }}
                axisLine={{ stroke: "var(--grid-line)" }}
                tickLine={false}
              />
              <YAxis
                domain={["auto", "auto"]}
                tick={{ fontSize: 12, fill: "var(--axis-text)", fontFamily: "var(--font-body)" }}
                axisLine={false}
                tickLine={false}
              />
              <ReferenceLine y={0} stroke="var(--grid-line)" />
              {totalSalesGraph.length > 0 && (
                <Tooltip
                  content={<CustomTooltip />}
                  followCursor
                  isAnimationActive={false}
                  cursor={{ fill: "var(--teal-soft)" }}
                />
              )}
              <Bar
                dataKey="amt"
                fill="url(#saleBarGradient)"
                radius={[4, 4, 0, 0]}
                barSize={56}
                label={{ position: "top", fill: "var(--ivory)", fontSize: 12 }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="ledger-rule" />

      <section>
        <h2 className="ledger-section-title">
          <span className="ledger-section-icon ledger-section-icon--blue">
            <LineChartOutlined />
          </span>
          Sale over the last 4 days
        </h2>
        <div className="ledger-panel">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={salesGraph?.length ? salesGraph : [{ date: "", amt: 0 }]}
              margin={{ top: 20, right: 12, left: 0, bottom: 5 }}
            >
              <defs>
                <linearGradient id="trendBarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7FA8F5" stopOpacity={1} />
                  <stop offset="100%" stopColor="#5B8DEF" stopOpacity={0.85} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 13, fill: "var(--axis-text)", fontFamily: "var(--font-body)" }}
                axisLine={{ stroke: "var(--grid-line)" }}
                tickLine={false}
              />
              <YAxis domain={[0, "auto"]} hide />
              <Tooltip
                content={<CustomTooltip />}
                followCursor
                isAnimationActive={false}
                cursor={{ fill: "var(--blue-soft)" }}
              />
              <Bar
                dataKey="amt"
                fill="url(#trendBarGradient)"
                radius={[4, 4, 0, 0]}
                barSize={48}
                label={{ position: "top", fill: "var(--ivory)", fontSize: 12 }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="ledger-rule" />

      <section>
        <h2 className="ledger-section-title">
          <span className="ledger-section-icon ledger-section-icon--violet">
            <ShopOutlined />
          </span>
          Purchases by commodity
        </h2>
        <div className="ledger-panel">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={
                totalPurchaseGraph.length
                  ? totalPurchaseGraph
                  : [{ date: "", amt: 0 }]
              }
              margin={{ top: 20, right: 12, left: 0, bottom: 5 }}
            >
              <defs>
                <linearGradient id="purchaseBarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#9C82E8" stopOpacity={1} />
                  <stop offset="100%" stopColor="#6C4BD6" stopOpacity={0.85} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--grid-line)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 13, fill: "var(--axis-text)", fontFamily: "var(--font-body)" }}
                axisLine={{ stroke: "var(--grid-line)" }}
                tickLine={false}
              />
              <YAxis domain={[0, "auto"]} hide />
              {totalPurchaseGraph.length > 0 && (
                <Tooltip
                  content={<CustomTooltip />}
                  followCursor
                  isAnimationActive={false}
                  cursor={{ fill: "var(--violet-soft)" }}
                />
              )}
              <Bar
                dataKey="amt"
                fill="url(#purchaseBarGradient)"
                radius={[4, 4, 0, 0]}
                barSize={56}
                label={{ position: "top", fill: "var(--ivory)", fontSize: 12 }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
};

export default Graphs;